import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../styles/shared";
import "./title";
import "./text";
import "./icon";
import "./list-item";
import { state } from "lit/decorators/state.js";
import { Icon } from "../../icons";
import { repeat } from "lit/directives/repeat.js";
import { query } from "lit/decorators/query.js";
import { promisifyAnimation } from "../util/util";
import { property } from "lit/decorators/property.js";
import { DirectiveResult } from "lit/async-directive";

export interface CardPaginationItem<T = any> {
	id: string;
	icon: Icon;
	name: DirectiveResult | string;
	data: T;
}

@customElement("bs-card-pagination")
export class BsCardPagination extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host,
				#items {
					display: flex;
					align-items: center;
					justify-content: center;
				}

				:host {
					flex-direction: column;
				}

				#card {
					width: 100%;
				}

				#items {
					flex-wrap: wrap;
					gap: var(--space-s);
					margin: 0 0 var(--space-l);
				}
			`
		];
	}

	@property({ type: Array }) items: CardPaginationItem[] = [];
	@property({ type: Function }) renderCard: (item: CardPaginationItem) => TemplateResult = () => html``;

	@state() selectedItemId?: string;
	@state() visibleItemId?: string;
	@query("#card") $card!: HTMLElement;

	protected updated(props: PropertyValues) {
		super.updated(props);

		if (props.has("items")) {
			this.selectedItemId = this.items[0]?.id;
			this.visibleItemId = this.items[0]?.id;
		}
	}

	async selectItemId(id: string) {
		if (id === this.selectedItemId) return;

		const prevIndex = this.items.findIndex((item) => this.selectedItemId === item.id);
		const nextIndex = this.items.findIndex((item) => id === item.id);
		if (nextIndex == null) return;

		const direction = nextIndex > (prevIndex ?? 0) ? "right" : "left";
		this.selectedItemId = id;

		const animationConfig: KeyframeAnimationOptions = {
			duration: 200,
			easing: "ease-in-out"
		};

		await promisifyAnimation(
			this.$card.animate(
				{
					opacity: [1, 0],
					transform: ["translateX(0)", `translateX(${direction === "right" ? "-" : ""}50px)`]
				},
				animationConfig
			)
		);

		this.visibleItemId = id;

		await promisifyAnimation(
			this.$card.animate(
				{
					opacity: [0, 1],
					transform: [`translateX(${direction === "right" ? "" : "-"}50px)`, `translateX(0)`]
				},
				animationConfig
			)
		);
	}

	renderQuote() {
		const { visibleItemId } = this;
		const item = this.items.find((item) => item.id === visibleItemId);
		if (item == null) return undefined;
		return this.renderCard(item);
	}

	render() {
		const { selectedItemId } = this;
		return html`
			<div id="items">
				${repeat(
					this.items,
					({ id, icon, name }) => html`
						<bs-list-item hoverable inline @click="${() => this.selectItemId(id)}" ?active="${id === selectedItemId}">
							<bs-icon id="icon" ?fill="${id === selectedItemId}" .icon="${icon}" size="small"></bs-icon>
							<bs-text>${name}</bs-text>
						</bs-list-item>
					`
				)}
			</div>
			<div id="card">${this.renderQuote()}</div>
		`;
	}
}
